<template>
	<checker-default-layout :showBreadcrumbs="false">
		<div class="home">
			<h1>UI Checker</h1>
			<checker-banner icon-size="l" class="home__banner">
				<span class="home__bannerText">
					The UI checker tool validates your HTML code against MOM Design
					Standards and Digital Service Standards.
					<br />
					This reviews up to 60% of MOM Design Standards and DSS compliance at code level.
					<br />
					Please view the report generated for other
					compliances.
				</span>
			</checker-banner>
			<div class="card__container">
				<div class="card">
					<span class="card__title">1. Enter project details</span>
					<span class="card__text">
						Provide details of your project.
					</span>
				</div>
				<div class="card">
					<span class="card__title">2. Upload HTML codes</span>
					<span class="card__text">
						Upload a ZIP file of your HTML codes. Exclude CSS or JS files.
					</span>
				</div>
				<div class="card">
					<span class="card__title">3. View your report</span>
					<span class="card__text">
						A report will be generated detailing your code's 
						compliances and solutions to assist you in 
						making the required changes.
					</span>
				</div>
			</div>
			<form class="home__form">
				<h2>Project details</h2>

				<div class="form__group">
					<label for="name">Project name</label>
					<input type="text" v-model="projectName" :class="{ error: projectNameState == 'error'}"/>
					<span>Enter the name of your project</span>
					<div v-if="projectNameState == 'error'" class="errorMessage">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm1-14a1 1 0 10-2 0 1 1 0 002 0zm-1 9a1 1 0 001-1v-5a1 1 0 10-2 0v5a1 1 0 001 1z" fill="currentColor"/></svg>
						<span>{{ projectNameMessage }}</span>
					</div>
				</div>

				<checker-checkbox
					v-model="uxActivities"
					:inputState="uxActivitiesState"
					:errMessage="uxActivitiesMessage"
					label="Indicate the UX activities conducted for your project."
					:options="[
						'Stakeholder interviews',
						'User surveys',
						'Focus group discussions',
						'User personas',
						'Journey maps',
						'User scenarios',
						'Card sorting activities',
						'Prototyping with Figma',
						'Usability testing',
						'UX self audit',
					]"
				/>

				<checker-radio
					label="Does your project make use of DX+ components and templates?"
					v-model="usedDxComponents"
					:inputState="usedDxComponentsState"
					:errMessage="usedDxComponentsMessage"
					:options="[
						{
							label: 'Yes',
							value: 'true',
						},
						{
							label: 'No',
							value: 'false',
						},
					]"
				/>

				<checker-radio
					label="Is your digital service an informational or transactional service?"
					v-model="serviceType"
					:value="serviceType"
					:inputState="serviceTypeState"
					:errMessage="serviceTypeMessage"
					:options="[
						{
							label: 'Informational service',
							value: 1,
						},
						{
							label: 'Transactional service',
							value: 2,
						},
					]"
				/>
				<checker-upload
					:url="urlConfig.API_FILE_URL"
					:headers="authHeader"
					label="Upload a ZIP file of your HTML codes."
					sub-text="zip file format only."
					hint="Please zip all your HTML pages and upload here."
					:input-state="filesState"
					v-model="files"
					:inputState="filesState"
					:errMessage="filesMessage"
					:maxTotalFileSize="10"
					@error="fileUploadError"
					@remove="removeFile"
					acceptedFiles="application/zip, multipart/x-zip, application/x-compressed, application/x-zip-compressed"
				/>

				<div class="declaration">
					<h3 class="declaration__title">Declaration</h3>
					<div class="declaration__body">
						
					</div>
					<checker-checkbox
						:options="['I confirm that no sensitive data or user data have been uploaded in the HTML codes.']"
						v-model="declaration"
						:inputState="declarationState"
						:errMessage="declarationMessage"
					/>
				</div>
                
				<button
					:class="[
						'form__button', 
						'form__button--primary',
						usedDxComponents==='false' && 'form__button--disabled'
					]"
					@click="submit"
					:disabled= "usedDxComponents==='false'"
				>
					Submit
				</button>
			</form>
		</div>
	</checker-default-layout>
</template>

<script>
import CheckerDefaultLayout from "@/components/page/CheckerDefaultLayout.vue";
import imageActions from "@/utils/imageActions";
import { urlConfig } from "@/constants/urlConfig";
import authHeader from "@/services/auth-header";
import CheckerBanner from "@/components/CheckerBanner.vue";
import CheckerCheckbox from "@/components/CheckerCheckbox.vue";
import CheckerRadio from "@/components/CheckerRadio.vue";
import CheckerUpload from "@/components/CheckerUpload.vue";
import VueRouter from "vue-router";

const { isNavigationFailure, NavigationFailureType } = VueRouter;

export default {
	name: "Home",
	components: {
		CheckerDefaultLayout,
		CheckerBanner,
		CheckerCheckbox,
		CheckerRadio,
		CheckerUpload,
	},
	mixins: [imageActions],
	data() {
		return {
			projectName: "",
			projectNameState: null,
			projectNameMessage: "",

			uxActivities: [],
			uxActivitiesState: null,
			uxActivitiesMessage: "",

			usedDxComponents: "",
			usedDxComponentsState: null,
			usedDxComponentsMessage: "",

			serviceType: "",
			serviceTypeState: null,
			serviceTypeMessage: "",
			
			files: [],
			filesState: null,
			filesMessage: "",

			declaration: [],
			declarationState: null,
			declarationMessage: "",
		};
	},
	computed: {
		urlConfig() {
			return urlConfig;
		},
		authHeader() {
			return authHeader();
		},
	},
	watch: {
		projectName(value) {
			this.projectName = value;
			this.vProjectName();
		},
		uxActivities(value) {
			this.uxActivities = value;

			this.vUxActivities();
		},
		usedDxComponents(value) {
			this.usedDxComponents = value;

			this.vUsedDxComponents();
		},
		serviceType(value) {
			console.log(this.serviceType)
			this.serviceType = value;
			console.log(value)
			
			this.vServiceType();
		},
		files(value) {
			this.files = value;

			this.vfiles();
		},
		declaration(value) {
			this.declaration = value;

			this.vDeclaration();
		},
	},
	methods: {
		vProjectName() {
			this.projectNameState = !this.projectName ? "error" : null;
			this.projectNameMessage = !this.projectName
				? "This field is required."
				: "";
		},
		vUxActivities() {
			this.uxActivitiesState = !this.uxActivities.length ? "error" : null;
			this.uxActivitiesMessage = !this.uxActivities.length
				? "This field is required."
				: "If your app does not utilise DX+ com";
		},
		vUsedDxComponents() {
			if (!this.usedDxComponents.length){
				this.usedDxComponentsState = "error"
				this.usedDxComponentsMessage = "This field is required."
			}else if(this.usedDxComponents ==='false'){
				this.usedDxComponentsState = "error"
				this.usedDxComponentsMessage = "If your digital service does not utilise DX+ components and templates, the UI checker tool will not be able to check comprehensively for compliances."
			}else{
				this.usedDxComponentsState = null
				this.usedDxComponentsMessage = ""
			}
		},
		vServiceType(){
			this.serviceTypeState = (this.serviceType == 1 || this.serviceType == 2) ? null : "error";

			this.serviceTypeMessage = (this.serviceType == 1 || this.serviceType == 2) ? "" : "This field is required.";
			console.log(this.serviceType)
			console.log(this.serviceType == 1 || this.serviceType == 2)
		},
		vfiles() {
			this.filesState =
				(!this.files.length) || (this.files.length > 1) ? "error" : null;

			if (!this.files.length) {
				this.filesMessage = "This field is required.";
			} else if (this.files.length > 1) {
				this.filesMessage = "Only one file is allowed";
			}
		},
		vDeclaration() {
			this.declarationState = !this.declaration.length ? "error" : null;
			this.declarationMessage = !this.declaration.length
				? "This field is required."
				: "";
		},
		fileUploadError(e) {
			this.filesState = "error";
			this.filesMessage =
				e === "invalidFileSize" ? "The file size is exeeding 10MB." : e;
		},
		removeFile(file) {
			console.log(file);

			var removeIndex = this.files
				.map(function(item) {
					return item.id;
				})
				.indexOf(file.id);
			this.files.splice(removeIndex, 1);

			// this.$store.dispatch("project/deleteFile", file.id).then(
			// 	() => {
					
			// 	},
			// 	(error) => {
			// 		this.loading = false;
			// 		this.message =
			// 			(error.response && error.response.data) ||
			// 			error.message ||
			// 			error.toString();
			// 		console.log(this.message);
			// 	}
			// );
		},
		submit(e) {
			e.preventDefault();

			this.vProjectName();
			this.vUxActivities();
			this.vUsedDxComponents();
			this.vfiles();
			this.vDeclaration();
			this.vServiceType();

			if(!this.projectNameState && !this.uxActivitiesState && !this.usedDxComponentsState && !this.declarationState && !this.filesState && !this.serviceTypeState){
				this.$store
					.dispatch("project/analyze", {
						projectName: this.projectName,
						uxActivities: this.uxActivities,
						usedDxComponents: this.usedDxComponents,
						file: {
							fileName: this.files[0].name,
							id: this.files[0].id,
						},
						declaration: this.declaration,
						ruleType : this.serviceType
					})
					.then(
						() => {
							this.$router.push("/Report").catch((failure) => {
								if (
									isNavigationFailure(
										failure,
										NavigationFailureType.redirected
									)
								) {
									// show a small notification to the user
									console.log(
										"Login in order to access the admin panel"
									);
								}
							});
						},
						(error) => {
							this.loading = false;
							this.message =
								(error.response && error.response.data) ||
								error.message ||
								error.toString();
							console.log(this.message);
						}
					);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
h1 {
	@include font-size(2xl);
	@include font-weight(semibold);
	@include line-height(xl);
}

h2 {
	@include font-size(2xl);
	@include font-weight(semibold);
	@include line-height(l);
}

.home {
	padding-top: rem(12);
	padding-bottom: rem(129);
	display: flex;
	flex-direction: column;
	gap: rem(20);
	max-width: rem(1088);
}

.home__form {
	display: flex;
	flex-direction: column;
	gap: rem(36);
}

.home__banner {
	padding: rem(24);
}

.home__bannerText {
	@include font-weight(semibold);
	@include font-size(m);
}

.card__container {
	display: flex;
	gap: rem(20);
}

.card {
	display: flex;
	flex-direction: column;
	border: 1px solid $color-cool-grey-30;
	border-top: 4px solid $color-blue-80;
	border-radius: 4px;
	padding: rem(16);
	flex: 1;
}

.card__title {
	@include font-size(l);
	@include font-weight(semibold);
	padding: rem(16) 0;
}

.card__text {
	@include font-size(s);
}

.form__group {
	display: flex;
	flex-direction: column;
	gap: rem(8);

	label {
		color: $color-cool-grey-100;
	}

	input {
		border-radius: 4px;
		border: 1px solid $color-cool-grey-20;
		padding: rem(9) rem(16);
		color: $color-cool-grey-50;
	}

	span {
		@include font-size(s);
		color: $color-cool-grey-70;
	}
}

.form__button {
	width: fit-content;

	&--primary {
		@include font-size(s);
		@include line-height(xl);
		background: $color-deep-purple;
		color: $color-white;
		padding: rem(4) rem(16);
		border-radius: 4px;
	}

	&--secondary {
		color: $color-deep-purple;
	}

	&--disabled {
		background-color: $color-grey-30;
		color: $color-white;
		border-color: $color-grey-30;
	}
}

.declaration {
	background: $color-grey-10;
	padding: rem(24) rem(44);
	$color: $color-cool-grey-80;
	display: flex;
	flex-direction: column;
	gap: rem(10);
}

.declaration__title {
	@include font-size(l);
	@include font-weight(semibold);
}

.declaration__list {
	list-style: disc inside;
}

input.error {
	border-color: $color-red-80;
}

.errorMessage {
	display: flex;
	color: $color-red-80;
	gap: rem(5);
	
	span {
		color: $color-red-80;
	}
}
</style>
